import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Hash, Download, CheckCircle2, AlertCircle, Clock, ShieldCheck, X, Plus, Upload, Layers, Trash2, FileText, Edit2, MoreVertical, History, ArrowUpDown, Receipt, CalendarDays } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { usePagination } from "@/lib/usePagination";
import GroupStatusBadge from "@/components/admin/GroupStatusBadge";

export default function AdminSGMTab() {
  const [requests, setRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Review/edit before generating hash
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rfc: "", permit_number: "", business_name: "", notes: "", issued_by: "" });
  const [generating, setGenerating] = useState(false);
  // Admin manual create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ user_id: "", rfc: "", permit_number: "", business_name: "", notes: "" });
  // Group create/edit
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ id: null, name: "", user_id: "", request_ids: [], notes: "" });
  // Group invoice upload
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceGroupId, setInvoiceGroupId] = useState(null);
  // Invoice history
  const [historyGroup, setHistoryGroup] = useState(null);
  const [historySortDesc, setHistorySortDesc] = useState(true);  // true = newest first
  // Complemento de pago dialog (clicking a history row opens this)
  const [complementoCtx, setComplementoCtx] = useState(null);  // { group, invoice }
  const [complementoForm, setComplementoForm] = useState({ file: null, payment_date: "", payment_amount: "", payment_method: "", notes: "" });
  const [savingComplemento, setSavingComplemento] = useState(false);
  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [invoiceForm, setInvoiceForm] = useState({ file: null, description: "", amount: "", period: currentMonth() });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, g, u] = await Promise.all([
        api.get("/admin/sgm-requests"),
        api.get("/admin/sgm-groups"),
        api.get("/admin/users"),
      ]);
      setRequests(r.data);
      setGroups(g.data);
      setClients(u.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/sgm-requests", {
        user_id: createForm.user_id,
        rfc: createForm.rfc.trim().toUpperCase(),
        permit_number: createForm.permit_number.trim().toUpperCase(),
        business_name: createForm.business_name,
        notes: createForm.notes,
      });
      toast.success("Solicitud creada");
      setCreateOpen(false);
      setCreateForm({ user_id: "", rfc: "", permit_number: "", business_name: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCreateGroup = () => {
    setGroupForm({ id: null, name: "", user_id: "", request_ids: [], notes: "" });
    setGroupOpen(true);
  };
  const openEditGroup = (g) => {
    setGroupForm({
      id: g.id,
      name: g.name || "",
      user_id: g.user_id || "",
      request_ids: g.request_ids || [],
      notes: g.notes || "",
    });
    setGroupOpen(true);
  };

  const submitGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.user_id) return toast.error("Selecciona un cliente");
    if (!groupForm.name) return toast.error("Indica un nombre para el grupo");
    try {
      if (groupForm.id) {
        // Editing — only name, notes, request_ids are mutable (client is fixed)
        await api.put(`/admin/sgm-groups/${groupForm.id}`, {
          name: groupForm.name,
          notes: groupForm.notes,
          request_ids: groupForm.request_ids,
        });
        toast.success("Grupo actualizado");
      } else {
        await api.post("/admin/sgm-groups", groupForm);
        toast.success("Grupo creado");
      }
      setGroupOpen(false);
      setGroupForm({ id: null, name: "", user_id: "", request_ids: [], notes: "" });
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`¿Eliminar grupo "${g.name}"? La factura asociada también se borrará.`)) return;
    try { await api.delete(`/admin/sgm-groups/${g.id}`); toast.success("Grupo eliminado"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceForm.file) return toast.error("Selecciona un PDF");
    const fd = new FormData();
    fd.append("file", invoiceForm.file);
    fd.append("description", invoiceForm.description);
    fd.append("amount", invoiceForm.amount || "0");
    fd.append("period", invoiceForm.period);
    try {
      await api.post(`/admin/sgm-groups/${invoiceGroupId}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura del grupo cargada");
      setInvoiceOpen(false);
      setInvoiceForm({ file: null, description: "", amount: "", period: currentMonth() });
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadGroupInvoice = async (g) => {
    try {
      const res = await api.get(`/admin/sgm-groups/${g.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = g.invoice_original_name || `factura-grupo-${g.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al descargar"); }
  };

  const downloadHistoryItem = async (group, item) => {
    try {
      const res = await api.get(`/admin/sgm-groups/${group.id}/invoices/${item.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = item.original_name || `factura-${item.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al descargar"); }
  };

  const downloadComplemento = async (group, invoice, comp) => {
    try {
      const res = await api.get(`/admin/sgm-groups/${group.id}/invoices/${invoice.id}/complementos/${comp.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = comp.original_name || `complemento-${comp.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al descargar"); }
  };

  const deleteComplemento = async (group, invoice, comp) => {
    if (!window.confirm(`¿Eliminar el complemento "${comp.original_name || comp.filename}"?`)) return;
    try {
      await api.delete(`/admin/sgm-groups/${group.id}/invoices/${invoice.id}/complementos/${comp.id}`);
      toast.success("Complemento eliminado");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadGroupSummary = async (group) => {
    try {
      const res = await api.get(`/admin/sgm-groups/${group.id}/summary-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `resumen-${group.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al generar resumen PDF"); }
  };

  const openComplemento = (group, invoice) => {
    setComplementoCtx({ group, invoice });
    setComplementoForm({
      file: null,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_amount: invoice.amount || "",
      payment_method: "",
      notes: "",
    });
  };

  const submitComplemento = async (e) => {
    e.preventDefault();
    if (!complementoForm.file) return toast.error("Seleccione el PDF del complemento");
    if (!complementoCtx) return;
    const fd = new FormData();
    fd.append("file", complementoForm.file);
    fd.append("payment_date", complementoForm.payment_date || "");
    fd.append("payment_amount", complementoForm.payment_amount || "0");
    fd.append("payment_method", complementoForm.payment_method || "");
    fd.append("notes", complementoForm.notes || "");
    setSavingComplemento(true);
    try {
      await api.post(
        `/admin/sgm-groups/${complementoCtx.group.id}/invoices/${complementoCtx.invoice.id}/complemento`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Complemento de pago subido y notificado por correo");
      setComplementoCtx(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingComplemento(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };
  const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

  const openReview = (r) => {
    setReviewTarget(r);
    setReviewForm({
      rfc: r.rfc || "",
      permit_number: r.permit_number || "",
      business_name: r.business_name || r.client_company || "",
      notes: r.notes || "",
      issued_by: "Equipo SGM México",
    });
    setReviewOpen(true);
  };

  const generateHash = async (e) => {
    e.preventDefault();
    if (!reviewTarget) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/sgm-requests/${reviewTarget.id}/approve`, {
        rfc: reviewForm.rfc.trim().toUpperCase(),
        permit_number: reviewForm.permit_number.trim().toUpperCase(),
        business_name: reviewForm.business_name,
        notes: reviewForm.notes,
        issued_by: reviewForm.issued_by,
      });
      toast.success(`Hash generado · ${data.hash_sha256?.slice(0, 12)}...`);
      setReviewOpen(false);
      setReviewTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const openReject = (r) => { setRejectTarget(r); setRejectReason(""); setRejectOpen(true); };
  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/sgm-requests/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Solicitud rechazada");
      setRejectOpen(false);
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/admin/sgm-requests/${r.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sgm-web-${r.folio}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al descargar"); }
  };

  const statusBadge = (s) => {
    if (s === "approved") return { c: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", l: "Aprobado", I: CheckCircle2 };
    if (s === "rejected") return { c: "text-red-400 border-red-400/40 bg-red-400/5", l: "Rechazado", I: AlertCircle };
    return { c: "text-amber-400 border-amber-400/40 bg-amber-400/5", l: "Pendiente", I: Clock };
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const requestsPager = usePagination(requests, 10, "sgm-requests-pager");

  return (
    <div data-testid="admin-sgm-tab">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h3 className="font-heading font-bold uppercase text-xl tracking-tight">
          <Hash className="inline w-5 h-5 text-[#F59E0B] mr-2 -mt-1" />
          Solicitudes SGM Web
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {pendingCount > 0 && (
            <span className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-1.5">
              {pendingCount} pendiente{pendingCount > 1 ? "s" : ""}
            </span>
          )}
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-1.5 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors" data-testid="sgm-admin-create-btn">
            <Plus className="w-3 h-3" /> Nueva Solicitud
          </button>
          <button onClick={openCreateGroup} className="inline-flex items-center gap-1.5 border border-[#2E2E33] text-[#A1A1AA] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors" data-testid="sgm-group-create-btn">
            <Layers className="w-3 h-3" /> Crear Grupo
          </button>
        </div>
      </div>

      <div className="border border-[#2E2E33] bg-[#1F1F25]">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          <div className="col-span-2">Folio</div>
          <div className="col-span-3">Cliente</div>
          <div className="col-span-2">RFC / Permiso</div>
          <div className="col-span-2">Hash</div>
          <div className="col-span-1">Estado</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-[#A1A1AA] font-mono text-sm">Cargando...</div>
        ) : requests.length === 0 ? (
          <div className="p-16 text-center">
            <Hash className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No hay solicitudes SGM Web.</p>
          </div>
        ) : (
          <>
          {requestsPager.pageItems.map((r) => {
            const sb = statusBadge(r.status);
            const SI = sb.I;
            return (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 sm:px-6 py-4 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] items-center" data-testid={`admin-sgm-row-${r.id}`}>
                <div className="md:col-span-2 font-mono text-sm text-white">{r.folio}</div>
                <div className="md:col-span-3 text-sm">
                  <div className="text-white">{r.client_name}</div>
                  <div className="font-mono text-xs text-[#A1A1AA] break-all">{r.client_email}</div>
                  {r.client_company && <div className="text-xs text-[#71717A]">{r.client_company}</div>}
                </div>
                <div className="md:col-span-2 text-xs font-mono text-[#A1A1AA]">
                  <div>{r.rfc}</div>
                  <div className="text-[#71717A] break-all">{r.permit_number}</div>
                </div>
                <div className="md:col-span-2 font-mono text-[10px] text-[#F59E0B] break-all">
                  {r.hash_sha256 ? `${r.hash_sha256.slice(0, 18)}...` : "—"}
                </div>
                <div className="md:col-span-1">
                  <span className={`inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${sb.c}`}>
                    <SI className="w-3 h-3" /> {sb.l}
                  </span>
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-2 flex-wrap">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => openReview(r)} title="Revisar y generar hash" className="border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black p-2 transition-colors" data-testid={`sgm-review-${r.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => openReject(r)} title="Rechazar" className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] p-2" data-testid={`sgm-reject-${r.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {r.status === "approved" && (
                    <button onClick={() => download(r)} className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`sgm-admin-download-${r.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {requestsPager.controls}
          </>
        )}
      </div>

      {/* Grupos por cliente */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-heading font-bold uppercase text-xl tracking-tight">
            <Layers className="inline w-5 h-5 text-[#F59E0B] mr-2 -mt-1" />
            Grupos (Agrupación por Cliente)
          </h3>
          <span className="font-mono text-xs text-[#71717A] uppercase tracking-widest">[ {groups.length} GRUPO{groups.length !== 1 ? "S" : ""} ]</span>
        </div>
        {groups.length === 0 ? (
          <div className="border border-[#2E2E33] bg-[#1F1F25] p-12 text-center">
            <Layers className="w-10 h-10 text-[#2E2E33] mx-auto mb-3" />
            <p className="text-[#A1A1AA] text-sm">Aún no hay grupos. Crea uno para agrupar solicitudes por cliente y subir su factura.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="sgm-groups-list">
            {groups.map((g) => (
              <div key={g.id} className="border border-[#2E2E33] bg-[#1F1F25]" data-testid={`sgm-group-${g.id}`}>
                <div className="px-5 py-4 border-b border-[#2E2E33] bg-[#26262C] flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-bold uppercase text-white">{g.name}</div>
                      <GroupStatusBadge financial={g.financial} />
                    </div>
                    <div className="font-mono text-xs text-[#A1A1AA] mt-1">{g.client_name} · {g.client_email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => openEditGroup(g)} title="Editar grupo (agregar/quitar solicitudes)" className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`sgm-group-edit-${g.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => { setInvoiceGroupId(g.id); setInvoiceOpen(true); }} title="Subir nueva factura" className="border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black p-2" data-testid={`sgm-group-upload-${g.id}`}>
                      <Upload className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setHistoryGroup(g)} title="Historial de facturas (trazabilidad)" className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-2" data-testid={`sgm-group-history-${g.id}`}>
                      <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteGroup(g)} title="Eliminar grupo" className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] p-2" data-testid={`sgm-group-delete-${g.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  {(g.requests || []).length === 0 ? (
                    <p className="text-[#71717A] text-xs font-mono">// Sin solicitudes asignadas</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {g.requests.map((r) => (
                        <span key={r.id} className="font-mono text-[10px] border border-[#2E2E33] bg-[#1A1A1F] px-2 py-1">
                          <span className="text-[#F59E0B]">{r.folio}</span> · {r.rfc}
                          {r.status === "approved" && <CheckCircle2 className="inline w-3 h-3 ml-1 text-emerald-400" />}
                        </span>
                      ))}
                    </div>
                  )}
                  {g.notes && <p className="text-[#A1A1AA] text-xs mt-3 italic">{g.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin create request dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">Nueva solicitud SGM Web</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Crear a nombre de un cliente registrado. Quedará pendiente para aprobar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <label className="overline block mb-2">Cliente *</label>
              <Select value={createForm.user_id} onValueChange={(v) => setCreateForm({ ...createForm, user_id: v })}>
                <SelectTrigger className="input-base rounded-none" data-testid="sgm-create-client"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id} className="font-mono text-sm">{c.email}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">RFC *</label>
                <input required minLength={10} maxLength={20} className="input-base font-mono uppercase" value={createForm.rfc} onChange={(e) => setCreateForm({ ...createForm, rfc: e.target.value.toUpperCase() })} data-testid="sgm-create-rfc" />
              </div>
              <div>
                <label className="overline block mb-2">Permiso *</label>
                <input required className="input-base font-mono uppercase" value={createForm.permit_number} onChange={(e) => setCreateForm({ ...createForm, permit_number: e.target.value.toUpperCase() })} data-testid="sgm-create-permit" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Razón Social</label>
              <input className="input-base" value={createForm.business_name} onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })} data-testid="sgm-create-business" />
            </div>
            <div>
              <label className="overline block mb-2">Notas</label>
              <textarea rows={2} className="input-base resize-none" value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} data-testid="sgm-create-notes" />
            </div>
            <DialogFooter className="gap-2">
              <button type="button" className="btn-ghost" onClick={() => setCreateOpen(false)}><X className="w-4 h-4" /> Cancelar</button>
              <button type="submit" className="btn-primary" data-testid="sgm-create-submit"><Plus className="w-4 h-4" /> Crear Solicitud</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Group create/edit dialog */}
      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-xl rounded-none p-0 flex flex-col max-h-[92vh]">
          <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
            <DialogTitle className="font-heading font-black uppercase">
              {groupForm.id ? "Editar grupo" : "Crear grupo de solicitudes"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {groupForm.id
                ? "Modifica el nombre o agrega/quita solicitudes del grupo."
                : "Elige un cliente y marca las solicitudes a agrupar (deben ser todas del mismo cliente)."}
            </DialogDescription>
          </DialogHeader>
          <form id="sgm-group-form" onSubmit={submitGroup} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div>
              <label className="overline block mb-2">Nombre del grupo *</label>
              <input required className="input-base" placeholder='Ej. "Lote enero 2026"' value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} data-testid="sgm-group-name" />
            </div>
            <div>
              <label className="overline block mb-2">Cliente {groupForm.id && <span className="text-[#71717A] text-[10px] normal-case tracking-normal">(no editable)</span>}</label>
              <Select value={groupForm.user_id} onValueChange={(v) => setGroupForm({ ...groupForm, user_id: v, request_ids: [] })} disabled={!!groupForm.id}>
                <SelectTrigger className="input-base rounded-none disabled:opacity-70" data-testid="sgm-group-client"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F25] border-[#2E2E33] text-white rounded-none">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id} className="font-mono text-sm">{c.email}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {groupForm.user_id && (
              <div>
                <label className="overline block mb-2">Solicitudes del cliente <span className="text-[#71717A] text-[10px] normal-case tracking-normal">({groupForm.request_ids.length} seleccionadas)</span></label>
                {(() => {
                  const clientReqs = requests.filter((r) => r.user_id === groupForm.user_id);
                  if (clientReqs.length === 0) {
                    return <p className="text-[#71717A] text-xs font-mono">// Este cliente no tiene solicitudes</p>;
                  }
                  return (
                    <div className="border border-[#2E2E33] bg-[#1A1A1F] max-h-72 overflow-y-auto divide-y divide-[#2E2E33]">
                      {clientReqs.map((r) => {
                        const checked = groupForm.request_ids.includes(r.id);
                        return (
                          <label key={r.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#26262C]">
                            <input type="checkbox" checked={checked} onChange={(e) => {
                              const next = e.target.checked
                                ? [...groupForm.request_ids, r.id]
                                : groupForm.request_ids.filter((x) => x !== r.id);
                              setGroupForm({ ...groupForm, request_ids: next });
                            }} className="w-4 h-4 accent-[#F59E0B]" data-testid={`sgm-group-req-${r.id}`} />
                            <div className="flex-1">
                              <div className="font-mono text-sm text-white">{r.folio}</div>
                              <div className="font-mono text-[10px] text-[#A1A1AA]">{r.rfc} · {r.permit_number} · <span className="text-[#F59E0B]">{r.status}</span></div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
            <div>
              <label className="overline block mb-2">Notas internas</label>
              <textarea rows={2} className="input-base resize-none" value={groupForm.notes} onChange={(e) => setGroupForm({ ...groupForm, notes: e.target.value })} data-testid="sgm-group-notes" />
            </div>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => setGroupOpen(false)}><X className="w-4 h-4" /> Cancelar</button>
            <button type="submit" form="sgm-group-form" className="btn-primary" data-testid="sgm-group-submit">
              <Layers className="w-4 h-4" /> {groupForm.id ? "Guardar Cambios" : "Crear Grupo"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group invoice upload dialog */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">Subir factura del grupo</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Asocia un PDF como evidencia de facturación del grupo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvoice} className="space-y-4">
            <div>
              <label className="overline block mb-2">Archivo PDF *</label>
              <input required type="file" accept="application/pdf" onChange={(e) => setInvoiceForm({ ...invoiceForm, file: e.target.files?.[0] || null })} className="input-base file:bg-[#F59E0B] file:text-black file:border-0 file:font-bold file:uppercase file:tracking-widest file:text-xs file:px-4 file:py-2 file:mr-4" data-testid="sgm-group-invoice-file" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Monto (MXN)</label>
                <input type="number" step="0.01" className="input-base" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} data-testid="sgm-group-invoice-amount" />
              </div>
              <div>
                <label className="overline block mb-2">Periodo</label>
                <input type="month" className="input-base" value={invoiceForm.period} onChange={(e) => setInvoiceForm({ ...invoiceForm, period: e.target.value })} data-testid="sgm-group-invoice-period" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Descripción</label>
              <input className="input-base" value={invoiceForm.description} onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })} data-testid="sgm-group-invoice-desc" />
            </div>
            <DialogFooter className="gap-2">
              <button type="button" className="btn-ghost" onClick={() => setInvoiceOpen(false)}><X className="w-4 h-4" /> Cancelar</button>
              <button type="submit" className="btn-primary" data-testid="sgm-group-invoice-submit"><Upload className="w-4 h-4" /> Subir</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review & Generate Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-2xl rounded-none p-0 flex flex-col max-h-[92vh]">
          <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
            <DialogTitle className="font-heading font-black uppercase tracking-tight">Revisar y generar hash</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {reviewTarget && (
                <>Folio <span className="font-mono text-[#F59E0B]">{reviewTarget.folio}</span> · {reviewTarget.client_name} ({reviewTarget.client_email})</>
              )}
            </DialogDescription>
          </DialogHeader>
          <form id="sgm-review-form" onSubmit={generateHash} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 text-xs text-[#D4D4D8] leading-relaxed">
              Puedes ajustar los datos antes de generar el hash. El hash SHA-256 se computa a partir de:
              <code className="block mt-2 font-mono text-[#F59E0B] text-[11px]">RFC | NÚMERO_PERMISO | FECHA_APROBACIÓN_UTC | FOLIO</code>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">RFC *</label>
                <input required minLength={10} maxLength={20} className="input-base font-mono uppercase"
                  value={reviewForm.rfc}
                  onChange={(e) => setReviewForm({ ...reviewForm, rfc: e.target.value.toUpperCase() })}
                  data-testid="sgm-review-rfc" />
              </div>
              <div>
                <label className="overline block mb-2">Número de Permiso *</label>
                <input required className="input-base font-mono uppercase"
                  value={reviewForm.permit_number}
                  onChange={(e) => setReviewForm({ ...reviewForm, permit_number: e.target.value.toUpperCase() })}
                  data-testid="sgm-review-permit" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Razón Social</label>
                <input className="input-base" value={reviewForm.business_name}
                  onChange={(e) => setReviewForm({ ...reviewForm, business_name: e.target.value })}
                  data-testid="sgm-review-business" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Observaciones (aparecen en el certificado)</label>
                <textarea rows={3} className="input-base resize-none" value={reviewForm.notes}
                  onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                  placeholder="Dejar vacío para usar el texto estándar"
                  data-testid="sgm-review-notes" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-2">Emitido por</label>
                <input className="input-base" value={reviewForm.issued_by}
                  onChange={(e) => setReviewForm({ ...reviewForm, issued_by: e.target.value })}
                  placeholder="Equipo SGM México" data-testid="sgm-review-issuer" />
              </div>
            </div>
            <div className="border-t border-[#2E2E33] pt-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-2">// Vista previa de la cadena de entrada</div>
              <div className="font-mono text-xs text-[#F59E0B] break-all bg-[#1A1A1F] border border-[#2E2E33] p-3" data-testid="sgm-review-preview">
                {(reviewForm.rfc || "?").toUpperCase()} | {(reviewForm.permit_number || "?").toUpperCase()} | [FECHA_AL_APROBAR] | {reviewTarget?.id?.slice(0, 8) || "..."}
              </div>
            </div>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => setReviewOpen(false)} data-testid="sgm-review-cancel">
              <X className="w-4 h-4" /> Cancelar
            </button>
            <button type="submit" form="sgm-review-form" disabled={generating} className="btn-primary disabled:opacity-50" data-testid="sgm-review-generate">
              {generating ? "Generando..." : "Generar Hash y Enviar"} <ShieldCheck className="w-4 h-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group invoice history dialog — bigger, sortable, clickable rows for complementos */}
      <Dialog open={!!historyGroup} onOpenChange={(o) => !o && setHistoryGroup(null)}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-5xl w-[95vw] rounded-none p-0 flex flex-col max-h-[92vh]" data-testid="sgm-group-history-dialog">
          {(() => {
            const liveGroup = historyGroup ? (groups.find((x) => x.id === historyGroup.id) || historyGroup) : null;
            const histRaw = (liveGroup?.invoices_history || []);
            const hist = [...histRaw].sort((a, b) => {
              const ta = new Date(a.uploaded_at || 0).getTime();
              const tb = new Date(b.uploaded_at || 0).getTime();
              return historySortDesc ? tb - ta : ta - tb;
            });
            const totalAmount = hist.reduce((s, i) => s + (Number(i.amount) || 0), 0);
            return (
              <>
                <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
                  <DialogTitle className="font-heading font-black uppercase flex items-center gap-2">
                    <History className="w-5 h-5 text-[#F59E0B]" /> Historial de Facturas
                  </DialogTitle>
                  <DialogDescription className="text-[#A1A1AA] flex items-center gap-3 flex-wrap mt-1">
                    {liveGroup && <span>Grupo <span className="font-mono text-[#F59E0B]">{liveGroup.name}</span> · {liveGroup.client_name}</span>}
                    {hist.length > 0 && (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
                        // {hist.length} factura{hist.length === 1 ? '' : 's'} · Total {formatMoney(totalAmount)}
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-between px-6 py-3 border-b border-[#2E2E33] bg-[#141418] gap-3 flex-wrap">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
                    Tip: clic en una factura para agregar complemento de pago
                  </div>
                  <div className="flex items-center gap-2">
                    {hist.length > 0 && (
                      <button
                        type="button"
                        onClick={() => downloadGroupSummary(liveGroup)}
                        className="inline-flex items-center gap-2 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
                        data-testid="sgm-history-export-pdf"
                        title="Exportar resumen del grupo en PDF (todas las facturas + complementos)"
                      >
                        <Download className="w-3 h-3" /> Exportar Resumen PDF
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setHistorySortDesc((v) => !v)}
                      className="inline-flex items-center gap-2 border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]"
                      data-testid="sgm-history-sort-toggle"
                    >
                      <ArrowUpDown className="w-3 h-3" />
                      Ordenar por fecha · {historySortDesc ? "Más recientes" : "Más antiguas"}
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  {hist.length === 0 ? (
                    <div className="border border-[#2E2E33] bg-[#1A1A1F] p-10 text-center">
                      <FileText className="w-10 h-10 text-[#2E2E33] mx-auto mb-3" />
                      <p className="text-[#A1A1AA] text-sm">Aún no se han subido facturas a este grupo.</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="sgm-group-history-list">
                      {hist.map((item) => {
                        const comps = item.complementos || [];
                        return (
                          <div key={item.id} className="border border-[#2E2E33] bg-[#1A1A1F] hover:border-[#F59E0B] transition-colors" data-testid={`sgm-group-history-item-${item.id}`}>
                            <button
                              type="button"
                              onClick={() => openComplemento(liveGroup, item)}
                              className="w-full text-left p-4 flex items-center justify-between gap-3 flex-wrap group cursor-pointer hover:bg-[#26262C]"
                              data-testid={`sgm-history-row-${item.id}`}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <FileText className="w-4 h-4 text-[#F59E0B] shrink-0" />
                                  <div className="font-mono text-sm text-white truncate">
                                    {item.invoice_number ? <span className="text-[#F59E0B]">{item.invoice_number} · </span> : null}
                                    {item.original_name || item.filename}
                                  </div>
                                </div>
                                <div className="font-mono text-[10px] text-[#A1A1AA] uppercase tracking-widest pl-6 flex items-center gap-2 flex-wrap">
                                  <CalendarDays className="w-3 h-3 text-[#71717A]" /> {formatDate(item.uploaded_at)}
                                  <span>·</span>
                                  <span className="text-[#F59E0B]">{formatMoney(item.amount)}</span>
                                  {item.period && <><span>·</span> <span>{item.period}</span></>}
                                  {comps.length > 0 && (
                                    <>
                                      <span>·</span>
                                      <span className="inline-flex items-center gap-1 border border-emerald-400/40 text-emerald-400 px-1.5 py-0.5">
                                        <Receipt className="w-2.5 h-2.5" /> {comps.length} complemento{comps.length === 1 ? '' : 's'}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {item.description && <div className="text-[#D4D4D8] text-xs mt-2 pl-6 italic">{item.description}</div>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); downloadHistoryItem(liveGroup, item); }}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); downloadHistoryItem(liveGroup, item); } }}
                                  className="inline-flex items-center gap-1.5 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest cursor-pointer"
                                  data-testid={`sgm-group-history-download-${item.id}`}
                                >
                                  <Download className="w-3.5 h-3.5" /> Descargar
                                </span>
                                <span className="inline-flex items-center gap-1.5 border border-[#2E2E33] group-hover:border-[#F59E0B] group-hover:text-[#F59E0B] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]" data-testid={`sgm-history-complemento-add-${item.id}`}>
                                  <Receipt className="w-3.5 h-3.5" /> Complemento
                                </span>
                              </div>
                            </button>
                            {comps.length > 0 && (
                              <div className="border-t border-[#2E2E33] bg-[#141418]/60 px-4 py-3 space-y-2">
                                {comps.map((c) => (
                                  <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap font-mono text-[11px]" data-testid={`sgm-history-complemento-${c.id}`}>
                                    <div className="flex items-center gap-2 text-emerald-400 min-w-0">
                                      <Receipt className="w-3 h-3 shrink-0" />
                                      <span className="text-white truncate">{c.original_name || c.filename}</span>
                                      <span className="text-[#71717A] hidden sm:inline">· {c.payment_date || formatDate(c.uploaded_at)} · {formatMoney(c.payment_amount)} · {c.payment_method || "—"}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); downloadComplemento(liveGroup, item, c); }}
                                        className="inline-flex items-center gap-1.5 border border-emerald-400/40 hover:bg-emerald-400/10 text-emerald-400 px-2 py-1 text-[10px] uppercase tracking-widest"
                                        data-testid={`sgm-history-complemento-download-${c.id}`}
                                      >
                                        <Download className="w-3 h-3" /> PDF
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); deleteComplemento(liveGroup, item, c); }}
                                        className="inline-flex items-center gap-1.5 border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] text-[#71717A] px-2 py-1 text-[10px] uppercase tracking-widest"
                                        data-testid={`sgm-history-complemento-delete-${c.id}`}
                                        title="Eliminar complemento"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
                  <button type="button" className="btn-ghost" onClick={() => setHistoryGroup(null)} data-testid="sgm-group-history-close">
                    <X className="w-4 h-4" /> Cerrar
                  </button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Complemento de pago dialog */}
      <Dialog open={!!complementoCtx} onOpenChange={(o) => !o && setComplementoCtx(null)}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-xl rounded-none p-0 flex flex-col max-h-[92vh]" data-testid="sgm-complemento-dialog">
          <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
            <DialogTitle className="font-heading font-black uppercase flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#F59E0B]" /> Complemento de Pago
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {complementoCtx && <>Factura <span className="font-mono text-[#F59E0B]">{complementoCtx.invoice.invoice_number || complementoCtx.invoice.original_name}</span> · {complementoCtx.group.name}</>}
            </DialogDescription>
          </DialogHeader>
          <form id="sgm-complemento-form" onSubmit={submitComplemento} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="sgm-complemento-form">
            <div>
              <label className="overline block mb-2">Archivo PDF del complemento *</label>
              <input
                required type="file" accept="application/pdf"
                onChange={(e) => setComplementoForm({ ...complementoForm, file: e.target.files?.[0] || null })}
                className="input-base file:bg-[#F59E0B] file:text-black file:border-0 file:font-bold file:uppercase file:tracking-widest file:text-xs file:px-4 file:py-2 file:mr-4"
                data-testid="sgm-complemento-file"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Fecha de pago</label>
                <input type="date" className="input-base" value={complementoForm.payment_date}
                  onChange={(e) => setComplementoForm({ ...complementoForm, payment_date: e.target.value })}
                  data-testid="sgm-complemento-date" />
              </div>
              <div>
                <label className="overline block mb-2">Monto pagado (MXN)</label>
                <input type="number" step="0.01" className="input-base" value={complementoForm.payment_amount}
                  onChange={(e) => setComplementoForm({ ...complementoForm, payment_amount: e.target.value })}
                  data-testid="sgm-complemento-amount" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Método de pago</label>
              <input className="input-base" placeholder="Transferencia, SPEI, cheque, efectivo..."
                value={complementoForm.payment_method}
                onChange={(e) => setComplementoForm({ ...complementoForm, payment_method: e.target.value })}
                data-testid="sgm-complemento-method" />
            </div>
            <div>
              <label className="overline block mb-2">Notas (opcional)</label>
              <textarea rows={3} className="input-base resize-none"
                value={complementoForm.notes}
                onChange={(e) => setComplementoForm({ ...complementoForm, notes: e.target.value })}
                placeholder="Referencia bancaria, banco emisor, observaciones..."
                data-testid="sgm-complemento-notes" />
            </div>
            <p className="text-[10px] font-mono text-[#71717A] uppercase tracking-widest border-t border-[#2E2E33] pt-3">
              // El cliente recibirá el complemento por correo automáticamente.
            </p>
          </form>
          <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
            <button type="button" className="btn-ghost" onClick={() => setComplementoCtx(null)} data-testid="sgm-complemento-cancel">
              <X className="w-4 h-4" /> Cancelar
            </button>
            <button type="submit" form="sgm-complemento-form" disabled={savingComplemento} className="btn-primary disabled:opacity-50" data-testid="sgm-complemento-submit">
              {savingComplemento ? "Subiendo..." : "Subir Complemento y Notificar"} <Receipt className="w-4 h-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">Rechazar solicitud</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {rejectTarget && `Folio ${rejectTarget.folio} · ${rejectTarget.client_email}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="space-y-3">
            <label className="overline block">Motivo del rechazo</label>
            <textarea required rows={3} className="input-base resize-none" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="sgm-reject-reason" />
            <DialogFooter className="gap-2">
              <button type="button" className="btn-ghost" onClick={() => setRejectOpen(false)}>
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button type="submit" className="btn-primary" data-testid="sgm-reject-confirm">
                Rechazar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
